import React, { useState, useEffect, useRef, useCallback } from "react";
import { Moment } from "moment";
import { replayFrameMs, replaySpeeds, Speed } from "../constants";
import IconPlay from "../common/IconPlay";
import IconPause from "../common/IconPause";
import IconReplay from "../common/IconReplay";
import IconPrev from "../common/IconPrev";
import IconNext from "../common/IconNext";

import "./PlayControls.css";
import Tooltip from "../common/Tooltip";

interface Props {
    addTime: (deltaS: number) => boolean;
    prevEvent: () => boolean;
    nextEvent: () => boolean;
    timeUpdated: (newTime: Moment) => void;
    setIsPlaying: (isPlaying: boolean) => void;
    from: Moment;
    to: Moment;
    currentTime: Moment;
}

const PlayControls: React.FC<Props> = function PlayControls({
    addTime,
    prevEvent,
    nextEvent,
    timeUpdated,
    setIsPlaying,
    to,
    from,
    currentTime
}: Props) {
    // time increment per animation step to achieve specified total duration
    const getTimeIncrement = (durationS: number): number => {
        const dt = to.diff(from, "seconds");
        const steps = durationS / (replayFrameMs * 1e-3);
        return dt / steps;
    };

    const [playing, setPlaying] = useState<boolean>(false);
    const [speed, setSpeed] = useState<Speed>(replaySpeeds[Math.floor(replaySpeeds.length / 2)]);
    const playingRef = useRef<boolean>(false);
    const timeIncrementRef = useRef<number>();

    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        playingRef.current = playing;

        if (playing) {
            timeIncrementRef.current = getTimeIncrement(speed.duration);
            intervalId = setInterval(() => {
                // using ref as value of playing is captured by closure
                if (playingRef.current) {
                    if (!addTime(timeIncrementRef.current!)) {
                        setPlaying(false);
                    }
                }
            }, replayFrameMs);
        }

        setIsPlaying(playing);

        return (): void => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [playing, addTime, setIsPlaying]); // eslint-disable-line

    const handleSpeedChange = (speed: Speed) => (): void => {
        setSpeed(speed);
        timeIncrementRef.current = getTimeIncrement(speed.duration);
    };

    const atEnd = to.isSame(currentTime);

    const handlePlayClick = useCallback(() => {
        if (!playing && atEnd) {
            timeUpdated(from);
        }
        setPlaying(!playing);
    }, [playing, atEnd, from, timeUpdated]);

    const handlePrevEvent = useCallback(() => {
        setPlaying(false);
        prevEvent();
    }, [prevEvent]);

    const handleNextEvent = useCallback(() => {
        setPlaying(false);
        nextEvent();
    }, [nextEvent]);

    useEffect(() => {
        const keydownListener = (ev: KeyboardEvent): void => {
            let consume = false;
            switch (ev.code) {
                case "ArrowRight":
                    handleNextEvent();
                    consume = true;
                    break;
                case "ArrowLeft":
                    handlePrevEvent();
                    consume = true;
                    break;
                case "Space":
                    handlePlayClick();
                    consume = true;
                    break;
            }
            if (consume) {
                ev.stopPropagation();
                ev.preventDefault();
            }
        };
        window.addEventListener("keydown", keydownListener);

        return (): void => {
            window.removeEventListener("keydown", keydownListener);
        };
    }, [handleNextEvent, handlePrevEvent, handlePlayClick]);

    return (
        <div className="play_controls">
            <div className="play_controls__speed tooltip">
                <Tooltip text={`Playback duration for the entire timeline`} delay></Tooltip>
                {replaySpeeds.map((s) => (
                    <div
                        key={s.duration}
                        className={`speed ${s === speed ? "active" : ""}`}
                        onClick={handleSpeedChange(s)}
                    >
                        {s.name}
                    </div>
                ))}
            </div>
            <div className="play_controls__button play_controls__button--inv tooltip" onClick={handlePrevEvent}>
                <Tooltip text="Previous event (Keyboard: Left Arrow)" delay>
                    <IconPrev />
                </Tooltip>
            </div>
            <div className="play_controls__button tooltip" onClick={handlePlayClick}>
                <Tooltip text="Play / pause (Keyboard: Space)" delay>
                    {playing ? <IconPause /> : to.isSame(currentTime) ? <IconReplay /> : <IconPlay />}
                </Tooltip>
            </div>
            <div className="play_controls__button play_controls__button--inv tooltip" onClick={handleNextEvent}>
                <Tooltip text="Next event (Keyboard: Right Arrow)" delay>
                    <IconNext />
                </Tooltip>
            </div>
            <div className={`play_controls__time ${playing ? "play_controls__time--active" : ""}`}>
                {currentTime.format("llll")}
            </div>
        </div>
    );
};

export default PlayControls;
